// src/pages/Home.tsx — one home screen.
//
// The built-in overview and the dashboards people ask for are the same page: a fixed
// header, a rail of views, and a body that swaps. Saved views are tabs rather than a
// gallery, and any of them can be made the view that opens on /app — so a finance
// director lands on arrears and a property manager lands on the overview, without
// either of them navigating.
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, ChevronDown, Home as HomeIcon, Loader2, MoreHorizontal, Pin, PinOff, Plus, Share2, Sparkles, SquarePen, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../stores/auth';
import { useCan, useCanPage } from '../hooks/usePermission';
import { Overview } from '../components/home/Overview';
import { DashboardView } from '../components/home/DashboardView';
import { Button, Field, Input, Modal } from '../components/ui';
import { Confirm, FormError } from '../components/forms/common';
import type { Dashboard } from '../types/api';

const EXAMPLES = [
  'Occupancy and rent collection across the London portfolio this year',
  'Everything I need on arrears — aged buckets, worst properties, trend',
  'Maintenance health: SLA performance, open jobs by age, spend by property',
  'Compliance risk — certificates expiring and deposits not yet protected',
];

/** How many view tabs sit on the rail before the rest fold into "More". */
const VISIBLE_TABS = 4;

export default function HomePage({ overview = false }: { overview?: boolean }) {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const restore = useAuth((s) => s.restore);

  // Hooks are called unconditionally, then combined — a permission check must never
  // decide whether a hook runs.
  const canDashboards = useCanPage('dashboards');
  const canCreate = useCan('dashboards.create');
  const canUpdate = useCan('dashboards.update');
  const canDelete = useCan('dashboards.delete');
  const canShare = useCan('dashboards.share');
  const canReadWorkOrders = useCan('workOrders.read');
  const canReadResidents = useCan('residents.read');
  const isResident = !!user && !user.allAccess && user.scope.type === 'OWN' && !canReadWorkOrders && canReadResidents;

  const [composing, setComposing] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<Dashboard | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [removing, setRemoving] = useState<Dashboard | null>(null);
  const [showMore, setShowMore] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);

  const { data: dashboards } = useQuery({
    queryKey: ['dashboards'],
    queryFn: async () => (await api.get<Dashboard[]>('/ai/dashboards')).data,
    enabled: canDashboards && !isResident,
  });

  const homeId = (user?.metadata?.preferences as { homeDashboardId?: string } | undefined)?.homeDashboardId ?? null;
  const list = dashboards ?? [];
  // /app honours the pinned view; /app/overview forces the built-in one.
  const activeId = id ?? (overview ? null : (homeId && list.some((d) => d.id === homeId) ? homeId : null));
  const active = list.find((d) => d.id === activeId) ?? null;

  const generate = useMutation({
    mutationFn: async (text: string) => (await api.post<Dashboard>('/ai/dashboards/generate', { prompt: text })).data,
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: ['dashboards'] }); setComposing(false); setPrompt(''); navigate(`/app/v/${d.id}`); },
  });
  const rename = useMutation({
    mutationFn: async () => api.patch(`/ai/dashboards/${renaming!.id}`, { name: renameTo.trim() }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dashboards'] }); setRenaming(null); },
  });
  const share = useMutation({
    mutationFn: async (d: Dashboard) => api.patch(`/ai/dashboards/${d.id}`, { isShared: !d.isShared }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboards'] }),
  });
  const remove = useMutation({
    mutationFn: async (d: Dashboard) => api.delete(`/ai/dashboards/${d.id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dashboards'] }); setRemoving(null); navigate('/app/overview'); },
  });
  const pin = useMutation({
    mutationFn: async (dashboardId: string | null) => api.patch('/auth/preferences', { homeDashboardId: dashboardId }),
    onSuccess: () => { void restore(); setMenuFor(null); },
  });

  // Close the per-view menu on any outside click.
  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent) => { if (!railRef.current?.contains(e.target as Node)) setMenuFor(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuFor]);

  if (isResident) return <Navigate to="/app/my-home" replace />;

  const firstName = user?.firstName ?? 'there';
  const hour = new Date().getHours();
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const visible = list.slice(0, VISIBLE_TABS);
  const overflow = list.slice(VISIBLE_TABS);
  const overflowActive = overflow.some((d) => d.id === activeId);

  const tabClass = (on: boolean) =>
    `relative whitespace-nowrap rounded-full px-3.5 py-1.5 text-[12.5px] transition ${on ? 'bg-ink-900 text-white' : 'text-ink-500 hover:bg-ink-100 hover:text-ink-900'}`;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <p className="eyebrow">{today}</p>
        <h1 className="display mt-2 text-[34px] text-ink-900 sm:text-[40px]">{hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'}, <em>{firstName}</em></h1>
        <p className="mt-1 text-[13.5px] text-ink-500">{user?.roleName} · {user?.region === 'AE' ? 'United Arab Emirates' : 'United Kingdom'}</p>
      </div>

      {canDashboards && (
        <div ref={railRef} className="flex flex-wrap items-center gap-1 border-b border-ink-100 pb-3">
          <Link to="/app/overview" className={tabClass(!activeId)}>Overview</Link>

          {visible.map((d) => (
            <span key={d.id} className="relative inline-flex items-center">
              <Link to={`/app/v/${d.id}`} className={tabClass(activeId === d.id)}>
                {homeId === d.id && <Pin className="mr-1 inline h-3 w-3 -translate-y-px" />}
                {d.name}
              </Link>
              {activeId === d.id && (canUpdate || canDelete) && (
                <button onClick={() => setMenuFor(menuFor === d.id ? null : d.id)} className="ml-0.5 rounded-full p-1 text-ink-400 transition hover:bg-ink-100 hover:text-ink-900" title="View options"><MoreHorizontal className="h-3.5 w-3.5" /></button>
              )}
              {menuFor === d.id && (
                <div className="absolute left-0 top-9 z-30 w-56 overflow-hidden rounded-2xl border border-ink-200 bg-white py-1.5 shadow-pop">
                  {canUpdate && <button onClick={() => { setRenaming(d); setRenameTo(d.name); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-ink-700 transition hover:bg-ink-50"><SquarePen className="h-3.5 w-3.5 text-ink-400" /> Rename</button>}
                  <button onClick={() => pin.mutate(homeId === d.id ? null : d.id)} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-ink-700 transition hover:bg-ink-50">
                    {homeId === d.id ? <><PinOff className="h-3.5 w-3.5 text-ink-400" /> Stop opening this first</> : <><HomeIcon className="h-3.5 w-3.5 text-ink-400" /> Make this my home view</>}
                  </button>
                  {canShare && <button onClick={() => { share.mutate(d); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-ink-700 transition hover:bg-ink-50"><Share2 className="h-3.5 w-3.5 text-ink-400" /> {d.isShared ? 'Stop sharing' : 'Share with my team'}</button>}
                  {canDelete && <button onClick={() => { setRemoving(d); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-red-600 transition hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                </div>
              )}
            </span>
          ))}

          {overflow.length > 0 && (
            <span className="relative">
              <button onClick={() => setShowMore((v) => !v)} className={tabClass(overflowActive)}>More <ChevronDown className="ml-0.5 inline h-3 w-3" /></button>
              {showMore && (
                <div className="absolute left-0 top-9 z-30 max-h-72 w-64 overflow-y-auto rounded-2xl border border-ink-200 bg-white py-1.5 shadow-pop">
                  {overflow.map((d) => (
                    <Link key={d.id} to={`/app/v/${d.id}`} onClick={() => setShowMore(false)} className="block truncate px-3.5 py-2 text-[12.5px] text-ink-700 transition hover:bg-ink-50">{d.name}</Link>
                  ))}
                </div>
              )}
            </span>
          )}

          {canCreate && (
            <button onClick={() => setComposing((v) => !v)} className={`${tabClass(composing)} ml-auto inline-flex items-center gap-1`}>
              <Plus className="h-3.5 w-3.5" /> New view
            </button>
          )}
        </div>
      )}

      {composing && canCreate && (
        <div className="relative overflow-hidden rounded-[22px] bg-ink-900 p-6 text-white shadow-float sm:p-7">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-bronze-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-300"><Sparkles className="h-3.5 w-3.5" /> New view</div>
            <div className="mt-4 flex items-end gap-3 rounded-2xl border border-white/15 bg-white/[0.06] p-2 transition focus-within:border-bronze-400/60">
              <textarea
                rows={2}
                autoFocus
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && prompt.trim()) generate.mutate(prompt.trim()); }}
                placeholder="e.g. Show me occupancy, arrears and maintenance spend for the London portfolio"
                className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2 text-[15px] leading-relaxed text-white outline-none placeholder:text-white/35"
              />
              <button onClick={() => prompt.trim() && generate.mutate(prompt.trim())} disabled={generate.isPending || !prompt.trim()} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-bronze-400 text-ink-900 transition hover:bg-white disabled:opacity-40" title="Create (⌘⏎)">
                {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {EXAMPLES.map((e) => (
                <button key={e} onClick={() => setPrompt(e)} className="rounded-full border border-white/15 px-3 py-1.5 text-[11.5px] text-white/70 transition hover:border-bronze-400 hover:text-white">{e}</button>
              ))}
            </div>
            {generate.isPending && <p className="mt-4 text-[12.5px] text-white/60">Choosing reports, chart types and layout…</p>}
            {generate.isError && <p className="mt-4 text-[12.5px] text-red-300">{(generate.error as Error)?.message ?? 'Could not build that view.'}</p>}
          </div>
        </div>
      )}

      {activeId && !active ? (
        <div className="py-12 text-center text-[13px] text-ink-400">Loading…</div>
      ) : active ? (
        <DashboardView dashboard={active} />
      ) : (
        <>
          <Overview />
          {canCreate && !list.length && !composing && (
            <p className="text-[12.5px] text-ink-500">
              Want a view of your own? <button onClick={() => setComposing(true)} className="font-medium text-bronze-600 hover:underline">Ask for one →</button>
            </p>
          )}
        </>
      )}

      <Modal open={!!renaming} onClose={() => setRenaming(null)} title="Rename this view"
        footer={<><Button variant="secondary" onClick={() => setRenaming(null)}>Cancel</Button><Button onClick={() => rename.mutate()} disabled={!renameTo.trim() || rename.isPending}>{rename.isPending ? 'Saving…' : 'Save'}</Button></>}>
        <div className="space-y-4">
          <Field label="Name"><Input value={renameTo} onChange={(e) => setRenameTo(e.target.value)} className="w-full" autoFocus /></Field>
          <FormError error={rename.error} />
        </div>
      </Modal>

      <Confirm open={!!removing} onClose={() => setRemoving(null)} title={`Delete “${removing?.name ?? ''}”?`}
        body="The view goes for everyone it was shared with. The reports behind it are untouched — you can always ask for it again."
        confirmLabel="Delete view" pending={remove.isPending} error={remove.error} onConfirm={() => remove.mutate(removing!)} />
    </div>
  );
}
